import React from 'react';
import { ChevronRight, ChevronLeft, Check, Mail, Calendar, X } from 'lucide-react';
import { usePergolaModels } from '../hooks/usePergolaModels';
import { supabase } from '../config/supabase';
import { PREMIUM_SIZES, PERGOLA_HEIGHT, getPremiumSize, getScreenFeet, getScreenPrice, roundMoney } from '../utils/pergolaCatalog';
import { countPrivacyPanels, getPrivacyPanelSides, isPrivacyPanelAddon } from '../utils/privacyPanels';

// ===== Types =====
type Model = string;

interface FinancingSubmissionData {
  name: string;
  email: string;
  phone: string;
  pergola_model: string;
  pergola_size: string;
  total_amount: number;
  required_deposit: number;
  financing_amount: number;
  source: string;
  referrer: string;
  user_agent: string;
  submissionId?: string | number;
}

interface FormData {
  model: Model;
  color: string;
  size: string;     // "10x12"
  location: string;
  siteWorkNotes: string;
  addOns: string[];
  addOnQuantities: Record<string, number>; // quantity for each add-on
  privacyPanelSides: Record<string, string[]>;
  name: string;
  email: string;
  phone: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
}



// ===== Constants =====
const currency = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);



const PERGOLA_COLORS = [
  { id: 'white', name: 'White', color: '#FFFFFF' },
  { id: 'dark-gray', name: 'Dark Gray', color: '#36454F' }
];

const SIZES = PREMIUM_SIZES.map(item => item.size);

const LOCATIONS = ['Deck', 'Patio', 'Concrete Pad', 'Grass', 'Other'] as const;

// Simple regex validators (client-side niceties only)
const CA_POSTAL = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[\d\s\-()+.]{8,}$/;

// ===== Helpers =====
const feetToMeters = (feet: number): number => Math.round(feet * 0.3048 * 10) / 10;

const formatSizeDisplay = (size: string, useMetric: boolean): { dimension: string; unit: string } => {
  const [width, length] = size.split('x').map(Number);
  if (useMetric) {
    const widthM = getPremiumSize(size)?.widthM ?? feetToMeters(width);
    const lengthM = getPremiumSize(size)?.depthM ?? feetToMeters(length);
    return { dimension: `${widthM}×${lengthM}`, unit: 'm' };
  }
  return { dimension: size, unit: 'ft' };
};

const normalizePostal = (v: string) => v.toUpperCase().replace(/\s+/g, '').replace(/^(.{3})(.*)$/, '$1 $2');

// Lightweight toast
function useToast() {
  const [msg, setMsg] = React.useState<string>('');
  React.useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(''), 2500);
    return () => clearTimeout(t);
  }, [msg]);
  const Toast = msg ? (
    <div className="fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-md shadow-lg z-50 text-sm">{msg}</div>
  ) : null;
  return { setMsg, Toast } as const;
}

// ===== Badge =====
const Badge: React.FC<{ children: React.ReactNode; tone?: 'blue' | 'amber' }> = ({ children, tone = 'blue' }) => {
  const tones = tone === 'amber' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-blue-100 text-blue-800 border-blue-200';
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold border rounded-full ${tones}`}>
      {children}
    </span>
  );
};

// ===== Component =====
const PergolaQuoteForm: React.FC = () => {
  const { models, addons, getPriceForSize, getCostPriceForSize, isLoading: pricingLoading, error: pricingError } = usePergolaModels();
  const [step, setStep] = React.useState(1);
  const [showNotInterestedOptions, setShowNotInterestedOptions] = React.useState(false);
  const [useMetric, setUseMetric] = React.useState(false);
  const [showExpensiveOptions, setShowExpensiveOptions] = React.useState(false);
  const [quickPhoneOpen, setQuickPhoneOpen] = React.useState(false);
  const [quickCallbackSubmitting, setQuickCallbackSubmitting] = React.useState(false);
  const [financingModalOpen, setFinancingModalOpen] = React.useState(false);
  const [financingContactInfo, setFinancingContactInfo] = React.useState({ name: '', email: '', phone: '' });
  const [financingSuccessOpen, setFinancingSuccessOpen] = React.useState(false);
  const [submittedFinancingData, setSubmittedFinancingData] = React.useState<FinancingSubmissionData | null>(null);
  const [contactSubmitSuccessOpen, setContactSubmitSuccessOpen] = React.useState(false);
  const [emailSending, setEmailSending] = React.useState(false);
  const [isStepping, setIsStepping] = React.useState(false);
  const [emailSent, setEmailSent] = React.useState(false);
  const [utm, setUtm] = React.useState({ source: '', medium: '', campaign: '', ref: '' });

  const [formData, setFormData] = React.useState<FormData>({
    model: '',
    color: '',
    size: '',
    location: '',
    siteWorkNotes: '',
    addOns: [],
    addOnQuantities: {},
    privacyPanelSides: { 'aluminum-panels': ['front'], 'privacy-shade': ['front'] },
    name: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
  });

  // Persist form (debounced) - temporarily disabled
  // React.useEffect(() => {
  //   if (typeof window !== 'undefined') {
  //     const id = setTimeout(() => {
  //       try {
  //         localStorage.setItem('evie-pergola-quote', JSON.stringify(formData));
  //       } catch {}
  //     }, 400);
  //     return () => clearTimeout(id);
  //   }
  // }, [formData]);

  // Capture UTM + referrer once
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const p = new URLSearchParams(window.location.search);
        setUtm({
          source: p.get('utm_source') || '',
          medium: p.get('utm_medium') || '',
          campaign: p.get('utm_campaign') || '',
          ref: document.referrer || ''
        });
      } catch { }
    }
  }, []);

  const { setMsg, Toast } = useToast();

  // ===== Handlers =====

  // Financing calculations
  const calculateMonthlyPayment = (principal: number, annualRate: number, months: number) => {
    if (annualRate === 0) {
      return principal / months;
    }
    const monthlyRate = annualRate / 12 / 100;
    return (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
  };



  const handleFinancingSubmit = async () => {
    if (!financingContactInfo.name || !financingContactInfo.email || !financingContactInfo.phone) {
      setMsg('Please fill in all contact information');
      return;
    }

    try {
      // Prepare financing inquiry data
      const inquiryData = {
        name: financingContactInfo.name,
        email: financingContactInfo.email,
        phone: financingContactInfo.phone,
        pergola_model: formData.model,
        pergola_size: formData.size,
        total_amount: totalMax,
        required_deposit: requiredDeposit,
        financing_amount: financingAmount,
        source: 'pergola_quote_form',
        referrer: document.referrer,
        user_agent: navigator.userAgent
      };

      // Submit to Supabase
      const { data, error } = await supabase
        .from('financing_inquiries')
        .insert([inquiryData])
        .select();

      if (error) {
        throw error;
      }

      console.log('Financing inquiry submitted:', data);

      // Store submitted data for success screen
      setSubmittedFinancingData({
        ...inquiryData,
        submissionId: data[0]?.id
      });

      // Close financing modal and show success screen
      setFinancingModalOpen(false);
      setFinancingSuccessOpen(true);
      setFinancingContactInfo({ name: '', email: '', phone: '' });

      // Google Analytics tracking
      if (typeof (window as any).gtag !== 'undefined') {
        (window as any).gtag('event', 'financing_inquiry_submitted', {
          event_category: 'financing',
          event_label: `${formData.model}_${formData.size}`,
          value: Math.round(financingAmount),
          custom_parameters: {
            pergola_model: formData.model,
            pergola_size: formData.size,
            total_amount: totalMax,
            financing_amount: financingAmount,
            required_deposit: requiredDeposit
          }
        });
      }
    } catch (error) {
      console.error('Error submitting financing inquiry:', error);
      setMsg('Error submitting request. Please try again.');
    }
  };

  const handleQuickCallback = async () => {
    if (!formData.phone.trim()) {
      setMsg('Please enter a phone number');
      return;
    }

    setQuickCallbackSubmitting(true);

    try {
      // Clean phone number
      const cleanPhone = formData.phone.replace(/\D/g, '');

      const insertData = {
        phone: cleanPhone,
        best_time: 'anytime',
        source: 'pergola_quote_form',
        notes: formData.name ? `Name: ${formData.name}` : undefined
      };

      const { error } = await supabase
        .from('quick_callbacks')
        .insert([insertData]);

      if (error) {
        console.error('Supabase error:', error);
        setMsg('Error submitting request. Please try again.');
      } else {
        setQuickPhoneOpen(false);
        setMsg('Thanks! We will call you back shortly.');
        // Clear the form fields
        setFormData(prev => ({ ...prev, phone: '', name: '' }));
      }
    } catch (error) {
      console.error('Error submitting callback request:', error);
      setMsg('Error submitting request. Please try again.');
    } finally {
      setQuickCallbackSubmitting(false);
    }
  };

  // Compute pricing pieces with useMemo to avoid unnecessary re-renders
  const { basePrice, addOnsTotal, installCost, deliveryFee, subtotal } = React.useMemo(() => {
    // Get base price from database (includes size and installation)
    let base = 0;
    if (formData.model && formData.size) {
      base = getPriceForSize(formData.model, formData.size);
    }

    const addOnsTotal = formData.addOns.reduce((sum, id) => {
      const a = addons.find(x => x.id === id);
      const quantity = isPrivacyPanelAddon(id)
        ? countPrivacyPanels(formData.size, formData.privacyPanelSides[id] || ['front'])
        : formData.addOnQuantities[id] || 1;
      return sum + (a?.quoteOnly ? 0 : a?.perLinearFoot ? getScreenPrice(formData.size, formData.privacyPanelSides[id] || ['front']) : (a?.price || 0) * quantity);
    }, 0);

    // Standard installation and delivery are included. Site work is quoted separately.
    const deckSurcharge = 0;
    const delivery = 0;
    const subtotal = roundMoney(base + addOnsTotal);

    return {
      basePrice: base,
      addOnsTotal,
      installCost: deckSurcharge, // Only deck surcharge since install is included in base
      deliveryFee: delivery,
      subtotal,
    };
  }, [formData.model, formData.size, formData.addOns, formData.addOnQuantities, formData.privacyPanelSides, getPriceForSize, addons]);

  // Apply tax once; special site work does not silently inflate the estimate.
  const hst = React.useMemo(() => roundMoney(subtotal * 0.13), [subtotal]);
  const totalWithHST = React.useMemo(() => roundMoney(subtotal + hst), [subtotal, hst]);
  const totalMin = React.useMemo(() => totalWithHST, [totalWithHST]);
  const totalMax = React.useMemo(() => totalWithHST, [totalWithHST]);

  // Financing calculations
  const calculateRequiredDeposit = React.useCallback(() => {
    if (!formData.model || !formData.size) {
      return Math.ceil((totalMax * 0.6) / 1000) * 1000; // Fallback
    }

    const baseCostPrice = getCostPriceForSize(formData.model, formData.size);

    // Add cost for add-ons (estimate at 60% of retail for add-ons)
    const addOnsCost = formData.addOns.reduce((sum, id) => {
      const addon = addons.find(a => a.id === id);
      if (!addon) return sum;

      const quantity = isPrivacyPanelAddon(id)
        ? countPrivacyPanels(formData.size, formData.privacyPanelSides[id] || ['front'])
        : formData.addOnQuantities[id] || 1;
      if (addon.quoteOnly) return sum;
      const retail = addon.perLinearFoot ? getScreenPrice(formData.size, formData.privacyPanelSides[id] || ['front']) : addon.price * quantity;
      return sum + roundMoney(retail / 2);
    }, 0);

    const totalCostPrice = baseCostPrice + addOnsCost;
    return Math.ceil(totalCostPrice / 1000) * 1000; // Round up to next $1000
  }, [formData.model, formData.size, formData.addOns, formData.addOnQuantities, formData.privacyPanelSides, totalMax, getCostPriceForSize, addons]);

  const { requiredDeposit, financingAmount, financingOptions } = React.useMemo(() => {
    const deposit = calculateRequiredDeposit();
    const financeAmount = totalMax - deposit;

    const options = [
      {
        term: 12,
        rate: 0,
        label: '12 months',
        description: '0% Interest',
        payment: calculateMonthlyPayment(financeAmount, 0, 12)
      },
      {
        term: 24,
        rate: 9,
        label: '24 months',
        description: '9% APR',
        payment: calculateMonthlyPayment(financeAmount, 9, 24)
      },
      {
        term: 36,
        rate: 9,
        label: '36 months',
        description: '9% APR',
        payment: calculateMonthlyPayment(financeAmount, 9, 36)
      }
    ];

    return {
      requiredDeposit: deposit,
      financingAmount: financeAmount,
      financingOptions: options
    };
  }, [totalMax, calculateRequiredDeposit]);

  const handleAddOnToggle = React.useCallback((id: string) => {
    setFormData(prev => {
      const isRemoving = prev.addOns.includes(id);
      const newAddOns = isRemoving ? prev.addOns.filter(x => x !== id) : [...prev.addOns, id];
      const newQuantities = { ...prev.addOnQuantities };
      if (isRemoving) {
        delete newQuantities[id];
      } else {
        newQuantities[id] = 1;
      }
      return { ...prev, addOns: newAddOns, addOnQuantities: newQuantities };
    });
  }, []);

  const handleQuantityChange = React.useCallback((addOnId: string, quantity: number) => {
    const maxQuantity = addOnId === 'heaters' ? 10 : 4;
    setFormData(prev => ({
      ...prev,
      addOnQuantities: {
        ...prev.addOnQuantities,
        [addOnId]: Math.max(1, Math.min(maxQuantity, quantity)),
      },
    }));
  }, []);

  // Scroll to top whenever step changes
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const nextStep = async () => {
    if (isStepping) return;
    setIsStepping(true);

    try {
      console.log('nextStep called, current step:', step);
      const currentStep = step;
      const nextStepNumber = Math.min(8, step + 1);

      console.log('Moving from step', currentStep, 'to step', nextStepNumber);

      // If moving from step 7 (contact info) to step 8 (final quote), save the data
      if (currentStep === 7 && nextStepNumber === 8) {
        console.log('Triggering database save for contact form submission');
        try {
          // Prepare quote submission data
          const quoteData = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            street_address: formData.streetAddress,
            city: formData.city,
            province: formData.province,
            postal_code: formData.postalCode,
            pergola_model: formData.model,
            pergola_size: formData.size,
            pergola_color: formData.color,
            pergola_location: formData.location,
            selected_addons: formData.addOns.map(id => {
              const option = addons.find(item => item.id === id);
              return isPrivacyPanelAddon(id)
                ? id + ' [' + (formData.privacyPanelSides[id] || []).join(', ') + '; ' + countPrivacyPanels(formData.size, formData.privacyPanelSides[id] || []) + ' panels' + (option?.perLinearFoot ? '; ' + getScreenFeet(formData.size, formData.privacyPanelSides[id] || []).toFixed(2) + ' linear ft' : '') + (option?.quoteOnly ? '; quote separately' : '') + ']'
                : id + ' [quantity ' + (formData.addOnQuantities[id] || 1) + (option?.quoteOnly ? '; quote separately' : '') + ']';
            }).concat(formData.siteWorkNotes.trim() ? ['Special site work (quoted separately): ' + formData.siteWorkNotes.trim()] : []),
            base_price: basePrice,
            addons_total: addOnsTotal,
            install_cost: installCost,
            delivery_fee: deliveryFee,
            subtotal: subtotal,
            hst: hst,
            total_with_hst: totalWithHST,
            total_min: totalMin,
            total_max: totalMax,
            action: 'contact-info-submitted',
            source: 'pergola_quote_form',
            referrer: document.referrer,
            user_agent: navigator.userAgent
          };

          console.log('Saving quote data on contact form submit:', quoteData);

          // Validate data types before sending
          const validatedData = {
            ...quoteData,
            base_price: Number(quoteData.base_price) || 0,
            addons_total: Number(quoteData.addons_total) || 0,
            install_cost: Number(quoteData.install_cost) || 0,
            delivery_fee: Number(quoteData.delivery_fee) || 0,
            subtotal: Number(quoteData.subtotal) || 0,
            hst: Number(quoteData.hst) || 0,
            total_with_hst: Number(quoteData.total_with_hst) || 0,
            total_min: Number(quoteData.total_min) || 0,
            total_max: Number(quoteData.total_max) || 0,
          };

          // Submit to Supabase
          const { error } = await supabase
            .from('quote_submissions')
            .insert([validatedData])
            .select();

          if (error) {
            console.error('Error saving contact form data:', error);
          } else {
            console.log('Contact form data saved successfully');
            // GA tracking
            if (typeof (window as any).gtag !== 'undefined') {
              (window as any).gtag('event', 'contact_info_submitted', {
                event_category: 'lead_generation',
                event_label: `${formData.model}_${formData.size}`,
                value: Math.round(totalWithHST)
              });

              // Google Ads conversion tracking
              (window as any).gtag('event', 'conversion', {
                'send_to': 'AW-17868365802/pBSECPKF4OobEOq_pshC',
                'value': 500.0,
                'currency': 'CAD'
              });
            }
          }
        } catch (error) {
          console.error('Error in contact form submission:', error);
        }
      }

      setStep(nextStepNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsStepping(false);
    }
  };

  const prevStep = () => {
    setStep(s => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getAvailableModels = () => models.filter(model => model.id === 'premium');

  const canProceed = () => {
    switch (step) {
      case 1: return Boolean(formData.size);
      case 2: return Boolean(formData.model);
      case 3: return Boolean(formData.color);
      case 4: return Boolean(formData.location);
      case 5: return true; // Add-ons step
      case 6: return true; // Quote summary step
      case 7: return Boolean(
        formData.name && EMAIL.test(formData.email) && PHONE.test(formData.phone) &&
        formData.streetAddress && formData.city && formData.province && CA_POSTAL.test(formData.postalCode)
      );
      case 8: return true;
      default: return false;
    }
  };

  // ===== UI sections =====
  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between text-sm text-gray-600 mb-2" aria-live="polite">
        <span>Step {step} of 7</span>
        <span>{Math.round((step / 7) * 100)}% Complete</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={(step / 7) * 100}>
        <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(step / 8) * 100}%` }} />
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Select Your Size</h2>
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            className={`px-3 py-1 text-sm font-medium rounded transition-all ${!useMetric ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            onClick={() => setUseMetric(false)}
          >Feet</button>
          <button
            type="button"
            className={`px-3 py-1 text-sm font-medium rounded transition-all ${useMetric ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            onClick={() => setUseMetric(true)}
          >Meters</button>
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
        {SIZES.map((size) => {
          const { dimension, unit } = formatSizeDisplay(size, useMetric);
          return (
            <button
              key={size}
              type="button"
              className={`p-4 rounded-lg border-2 transition-all ${formData.size === size ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}
              onClick={() => setFormData(prev => {
                const next = { ...prev, size } as FormData;
                next.model = 'premium';
                return next;
              })}
            >
              <div className="text-lg font-semibold">{dimension}</div>
              <div className="text-sm text-gray-500">{unit}</div>
            </button>
          );
        })}
      </div>
      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">Foot dimensions are approximate; exact metric dimensions determine pricing. Height: {PERGOLA_HEIGHT}. Custom sizes and wall-mounted pergolas require an individual quote.</p>
    </div>
  );

  const renderStep2 = () => {
    const availableModels = getAvailableModels();
    const badgeTone = (label?: string) => (label === 'Best Value' ? 'amber' : 'blue') as 'amber' | 'blue';

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Choose Your Pergola Model</h2>

        <p className="text-gray-600">Premium includes motorized louvers, perimeter lighting, standard installation and delivery. Height: {PERGOLA_HEIGHT}.</p>

        <div className="grid md:grid-cols-2 gap-6">
          {availableModels.map((model) => (
            <div
              key={model.id}
              className={`border-2 rounded-xl p-6 cursor-pointer transition-all shadow-sm ${formData.model === model.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
              onClick={() => setFormData(prev => ({ ...prev, model: model.id }))}
              role="button"
              aria-pressed={formData.model === model.id}
              tabIndex={0}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{model.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{model.description}</p>
                </div>
                {model.badge && <Badge tone={badgeTone(model.badge)}>{model.badge}</Badge>}
              </div>

              <p className="mt-4 text-gray-900 font-semibold">{model.tagline}</p>

              <ul className="mt-3 space-y-1 text-sm text-gray-700">
                {model.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-[3px] h-2 w-2 rounded-full bg-blue-500" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-white border">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Best for</div>
                  <div className="text-sm text-gray-800">{model.bestFor}</div>
                </div>
                <div className="p-3 rounded-lg bg-white border">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Includes</div>
                  <div className="text-sm text-gray-800">{formData.model === model.id ? model.includes.join(' • ') : `${model.includes.slice(0, 2).join(' • ')}…`}</div>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Installed price before tax</div>
                  <div className="text-2xl font-bold text-gray-900">{new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(formData.model === model.id && formData.size ? basePrice : model.price)}</div>
                  <div className="text-xs text-gray-500 italic mt-1">(standard installation and delivery included)</div>
                </div>
                {formData.model === model.id ? (
                  <Check className="text-blue-600" size={22} aria-hidden />
                ) : (
                  <button type="button" className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700">Select</button>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                <span className="px-2 py-1 bg-gray-100 rounded">10-Year Warranty</span>
                <span className="px-2 py-1 bg-gray-100 rounded">Pro Install Available</span>
                <span className="px-2 py-1 bg-gray-100 rounded">Financing (O.A.C.)</span>
              </div>
            </div>
          ))}
          <article className="border-2 border-gray-200 rounded-xl p-6 shadow-sm flex flex-col">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Premium Plus</h3>
                <p className="text-sm text-gray-600 mt-1">A heavier-duty system tailored to your project.</p>
              </div>
              <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">Individual quote</span>
            </div>
            <p className="mt-4 text-gray-900 font-semibold">Greater span capability for your outdoor space.</p>
            <ul className="mt-3 space-y-2 text-sm text-gray-700">
              {['150 kg/m² / 30.7 psf load rating', 'Heavier-duty custom structural system', 'Greater span capability'].map(feature => (
                <li key={feature} className="flex items-start gap-2">
                  <span className="mt-[3px] h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 p-3 rounded-lg bg-gray-50 border">
              <div className="text-xs uppercase tracking-wide text-gray-500">Best for</div>
              <div className="text-sm text-gray-800">Projects requiring a heavier-duty structure or greater spans</div>
            </div>
            <div className="mt-auto pt-6">
              <div className="text-xs uppercase tracking-wide text-gray-500">Pricing</div>
              <div className="text-2xl font-bold text-gray-900">Individually quoted</div>
              <p className="mt-2 text-sm text-gray-600">Tell us about your space so we can prepare a quote for your project.</p>
              <a href="/pergola-quote?model=premium-plus" aria-label="Request a Premium+ Individual Quote" className="mt-4 block rounded-lg bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
                Request an Individual Quote
              </a>
            </div>
          </article>
        </div>

        <div className="mt-2 text-xs text-gray-500">Prices shown are starting points and vary with size, add-ons, and installation complexity.</div>


      </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Choose Your Color</h2>
      <p className="text-lg text-gray-600">Select the color that matches your style</p>

      <div className="grid md:grid-cols-2 gap-4">
        {PERGOLA_COLORS.map((color) => (
          <button
            key={color.id}
            type="button"
            className={`p-4 border-2 rounded-lg text-left transition-all flex items-center gap-4 ${formData.color === color.id
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
              }`}
            onClick={() => {
              setFormData(prev => ({ ...prev, color: color.id }));
            }}
          >
            <div
              className="w-12 h-12 rounded-full border-2 border-gray-300"
              style={{
                backgroundColor: color.color,
                border: color.id === 'white' ? '2px solid #D1D5DB' : '2px solid #6B7280'
              }}
            ></div>
            <div>
              <div className="font-semibold">{color.name}</div>
              <div className="text-sm text-gray-600">Powder-coated aluminum</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Installation Location</h2>
      <div className="space-y-3">
        {LOCATIONS.map((location) => (
          <button
            key={location}
            type="button"
            className={`w-full p-4 text-left rounded-lg border-2 transition-all ${formData.location === location ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setFormData(prev => ({ ...prev, location }))}
          >
            <div className="flex justify-between items-center">
              <span className="font-medium">{location}</span>
              {formData.location === location && <Check className="text-blue-600" size={20} aria-hidden />}
            </div>
          </button>
        ))}
      </div>
      <label className="block text-sm text-gray-700">Special site work (optional)
        <textarea className="mt-2 w-full border rounded-lg p-3" maxLength={2000} value={formData.siteWorkNotes} onChange={e => setFormData(prev => ({ ...prev, siteWorkNotes: e.target.value }))} placeholder="Tell us about footings, reinforcement, demolition or access requirements" />
      </label>
      <p className="text-sm text-gray-600">Standard installation and delivery are included. Any special site work is assessed and priced separately before approval.</p>
      {formData.location === 'Deck' && (
        <p className="text-xs text-gray-600">Deck reinforcement, new footings and other special site work are quoted separately after assessment.</p>
      )}
    </div>
  );

  const renderStep5 = () => {

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Add-Ons</h2>
        <div className="space-y-4">
          {addons.map((a) => {
            const isPremiumOnly = false;
            const isDisabled = isPremiumOnly;
            const quantity = isPrivacyPanelAddon(a.id)
              ? countPrivacyPanels(formData.size, formData.privacyPanelSides[a.id] || ['front'])
              : formData.addOnQuantities[a.id] || 1;
            const optionTotal = a.perLinearFoot ? getScreenPrice(formData.size, formData.privacyPanelSides[a.id] || ['front']) : a.price * quantity;

            return (
              <div key={a.id} className={`border-2 rounded-lg p-4 transition-all ${isPremiumOnly
                ? 'border-gray-200 bg-gray-50 opacity-60'
                : formData.addOns.includes(a.id)
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200'
                }`}>
                <div className="flex items-start justify-between">
                  <label className={`flex items-center flex-1 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={formData.addOns.includes(a.id)}
                      onChange={() => !isDisabled && handleAddOnToggle(a.id)}
                      disabled={isDisabled}
                      className={`w-5 h-5 mr-3 ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600'}`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${isPremiumOnly ? 'text-gray-500' : ''}`}>{a.name}</span>
                        {isPremiumOnly && (
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">Premium Only</span>
                        )}
                      </div>
                      {a.hasQuantity && !isPrivacyPanelAddon(a.id) && formData.addOns.includes(a.id) && !isDisabled && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-sm text-gray-600">{a.id === 'heaters' ? 'Quantity:' : 'Sides:'}</span>
                          <div className="flex items-center border border-gray-300 rounded">
                            <button type="button" className="px-2 py-1 hover:bg-gray-100 text-gray-600" onClick={() => handleQuantityChange(a.id, (formData.addOnQuantities[a.id] || 1) - 1)}>−</button>
                            <input
                              type="number"
                              min={1}
                              max={a.id === 'heaters' ? 10 : 4}
                              value={formData.addOnQuantities[a.id] || 1}
                              onChange={(e) => handleQuantityChange(a.id, parseInt(e.target.value, 10) || 1)}
                              className="w-12 text-center border-0 focus:ring-0 py-1"
                            />
                            <button type="button" className="px-2 py-1 hover:bg-gray-100 text-gray-600" onClick={() => handleQuantityChange(a.id, (formData.addOnQuantities[a.id] || 1) + 1)}>+</button>
                          </div>
                          {(a.id === 'privacy-shade' || a.id === 'aluminum-panels') && (
                            <span className="text-xs text-gray-500">(max 4 sides)</span>
                          )}
                        </div>
                      )}
                    </div>
                  </label>
                  <div className="text-right">
                    {a.quoteOnly ? (<span className="font-semibold text-blue-700">Quoted separately</span>) : a.price === 0 ? (
                      <div className="flex flex-col items-end">
                        <span className={`font-bold text-lg ${isPremiumOnly ? 'text-gray-500' : 'text-green-600'}`}>Included</span>

                      </div>
                    ) : (
                      <>
                        <span className={`font-bold text-lg ${isPremiumOnly ? 'text-gray-500' : ''}`}>
                          +{currency(formData.addOns.includes(a.id) ? optionTotal : a.price)}
                          {!isPrivacyPanelAddon(a.id) && a.hasQuantity && formData.addOns.includes(a.id) && !isDisabled && (
                            <span className="text-sm text-gray-600"> each</span>
                          )}
                        </span>
                        {isPrivacyPanelAddon(a.id) && (
                          <div className="text-sm text-gray-600">{currency(a.price)} {a.perLinearFoot ? 'per linear ft' : 'per panel'}</div>
                        )}
                        {!isPrivacyPanelAddon(a.id) && a.hasQuantity && formData.addOns.includes(a.id) && !isDisabled && quantity > 1 && (
                          <div className="text-sm font-semibold text-blue-600">Total: {currency(optionTotal)}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {isPrivacyPanelAddon(a.id) && formData.addOns.includes(a.id) && (
                  <fieldset className="mt-3 border-t pt-3">
                    <legend className="text-sm font-medium">Sides to cover</legend>
                    <p className="text-xs text-gray-600 mb-2">Each panel covers up to 12 ft. Longer sides require multiple panels.</p>
                    <div className="grid grid-cols-2 gap-2">
                      {getPrivacyPanelSides(formData.size).map(side => (
                        <label key={side.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.privacyPanelSides[a.id].includes(side.id)}
                            disabled={formData.privacyPanelSides[a.id].length === 1 && formData.privacyPanelSides[a.id].includes(side.id)}
                            onChange={() => setFormData(prev => ({
                              ...prev,
                              privacyPanelSides: {
                                ...prev.privacyPanelSides,
                                [a.id]: prev.privacyPanelSides[a.id].includes(side.id)
                                  ? prev.privacyPanelSides[a.id].filter(id => id !== side.id)
                                  : [...prev.privacyPanelSides[a.id], side.id],
                              },
                            }))}
                          />
                          {side.label} ({side.length} ft): {side.panels} {side.panels === 1 ? 'panel' : 'panels'}
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{a.perLinearFoot ? `${getScreenFeet(formData.size, formData.privacyPanelSides[a.id]).toFixed(2)} linear ft, based on exact metric dimensions. ` : ''}Panels cover up to 12 ft each.</p>
                    <p className="mt-2 text-sm font-semibold">{quantity} {quantity === 1 ? 'panel' : 'panels'} for {formData.privacyPanelSides[a.id].length} selected {formData.privacyPanelSides[a.id].length === 1 ? 'side' : 'sides'}</p>
                  </fieldset>
                )}
              </div>
            );
          })}
        </div>



        <div className="mt-8 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
          <h3 className="font-semibold text-orange-900 mb-2">🔥 Complete Your Space</h3>
          <p className="text-orange-800">Consider adding Awnings to create the perfect outdoor oasis!</p>
        </div>
      </div>
    );
  };

  const renderStep6 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Almost There!</h2>
      <p className="text-lg text-gray-600">Just a few details so we can send your quote and schedule your consultation.</p>

      <div className="text-xs text-gray-600">★★★★★ 4.9/5 from Ontario homeowners • "Install was flawless." • "Snow didn't faze it."</div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="name">Name *</label>
          <input
            id="name"
            type="text"
            required
            autoComplete="name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="phone">Phone *</label>
          <input
            id="phone"
            type="tel"
            required
            inputMode="tel"
            autoComplete="tel"
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.phone && !PHONE.test(formData.phone) ? 'border-red-400' : 'border-gray-300'}`}
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email">Email *</label>
        <input
          id="email"
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.email && !EMAIL.test(formData.email) ? 'border-red-400' : 'border-gray-300'}`}
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Installation Address</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="streetAddress">Street Address *</label>
          <input
            id="streetAddress"
            type="text"
            required
            autoComplete="address-line1"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="123 Main Street"
            value={formData.streetAddress}
            onChange={(e) => setFormData(prev => ({ ...prev, streetAddress: e.target.value }))}
          />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="city">City *</label>
            <input
              id="city"
              type="text"
              required
              autoComplete="address-level2"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Toronto"
              value={formData.city}
              onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="province">Province *</label>
            <select
              id="province"
              required
              autoComplete="address-level1"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.province}
              onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
            >
              <option value="">Select Province</option>
              <option value="AB">Alberta</option>
              <option value="BC">British Columbia</option>
              <option value="MB">Manitoba</option>
              <option value="NB">New Brunswick</option>
              <option value="NL">Newfoundland and Labrador</option>
              <option value="NT">Northwest Territories</option>
              <option value="NS">Nova Scotia</option>
              <option value="NU">Nunavut</option>
              <option value="ON">Ontario</option>
              <option value="PE">Prince Edward Island</option>
              <option value="QC">Quebec</option>
              <option value="SK">Saskatchewan</option>
              <option value="YT">Yukon</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="postalCode">Postal Code *</label>
            <input
              id="postalCode"
              type="text"
              required
              autoComplete="postal-code"
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.postalCode && !CA_POSTAL.test(formData.postalCode) ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="M5V 3A8"
              value={formData.postalCode}
              onChange={(e) => setFormData(prev => ({ ...prev, postalCode: normalizePostal(e.target.value) }))}
            />
          </div>
        </div>

        <p className="text-sm text-gray-600">This helps us estimate delivery and installation logistics.</p>

        <div className="mt-6 p-3 bg-gray-50 rounded-lg text-sm">
          <p className="mb-2">Too busy for forms? <button type="button" className="underline text-blue-700" onClick={() => setQuickPhoneOpen(true)}>Just drop your phone number</button> — we'll call you back.</p>
          <p className="text-gray-600">Or call us directly: <a href="tel:+19056745983" className="text-accent hover:text-green-700 font-semibold">(905) 674-5983</a> | <a href="tel:+18554744653" className="text-accent hover:text-green-700 font-semibold">(855) 474-4653</a></p>
        </div>


      </div>

      {quickPhoneOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-5 w-[90%] max-w-sm shadow-xl">
            <h4 className="font-semibold mb-3">We will call you back</h4>
            <div className="space-y-3">
              <input
                placeholder="Phone number"
                className="w-full p-3 border border-gray-300 rounded-lg"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              />
              <input
                placeholder="Name"
                className="w-full p-3 border border-gray-300 rounded-lg"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
              <div className="flex gap-2">
                <button
                  className="flex-1 py-2 rounded-lg bg-gray-200"
                  onClick={() => setQuickPhoneOpen(false)}
                  disabled={quickCallbackSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-2 rounded-lg bg-blue-600 text-white disabled:bg-blue-400"
                  onClick={handleQuickCallback}
                  disabled={quickCallbackSubmitting}
                >
                  {quickCallbackSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep7 = () => {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Pergola Summary</h2>
          <p className="text-lg text-gray-600">You've designed the perfect outdoor space!</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-900">{formatSizeDisplay(formData.size, useMetric).dimension || '—'}</div>
            <div className="text-sm text-blue-700">{useMetric ? 'Metres' : 'Approx. feet'} · Height: {PERGOLA_HEIGHT}</div>
          </div>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-900">{formData.model || '—'}</div>
            <div className="text-sm text-green-700">{formData.color} Color</div>
          </div>
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg text-center">
            <div className="text-2xl font-bold text-purple-900">{formData.addOns.length}</div>
            <div className="text-sm text-purple-700">Add-ons Selected</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-lg text-center">
          <p className="text-lg mb-3">Your Estimate</p>
          <div className="space-y-1 mb-3">
            <div className="flex justify-between items-center text-sm opacity-90">
              <span>Priced items subtotal:</span>
              <span>{currency(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-sm opacity-90">
              <span>HST (13%):</span>
              <span>{currency(hst)}</span>
            </div>
            <div className="border-t border-white/30 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total:</span>
                <span className="text-3xl font-bold">{currency(totalWithHST)}</span>
              </div>
            </div>
          </div>
          <p className="text-sm opacity-90">Unpriced options are excluded from this estimate. Includes standard installation and delivery. Special site work is quoted separately.</p>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-3 text-xs">
            <span className="px-2 py-1 bg-white/20 rounded">CSA Electrical • Insured</span>
            <span className="px-2 py-1 bg-white/20 rounded">Ontario Installers</span>
            <span className="px-2 py-1 bg-white/20 rounded">10-yr Warranty</span>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Why Choose Evie Outdoors?</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold text-sm">✓</span>
                </div>
                <span className="font-semibold text-gray-900">Professional Installation</span>
              </div>
              <p className="text-gray-600 text-sm ml-11">10-year warranty included</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold text-sm">✓</span>
                </div>
                <span className="font-semibold text-gray-900">Flexible Financing</span>
              </div>
              <p className="text-gray-600 text-sm ml-11">Options available (O.A.C.)</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold text-sm">✓</span>
                </div>
                <span className="font-semibold text-gray-900">Premium Construction</span>
              </div>
              <p className="text-gray-600 text-sm ml-11">Aluminum built for Canadian weather</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold text-sm">✓</span>
                </div>
                <span className="font-semibold text-gray-900">Upgrade Ready</span>
              </div>
              <p className="text-gray-600 text-sm ml-11">Design for future add-ons</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-colors md:col-span-2">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold text-sm">✓</span>
                </div>
                <span className="font-semibold text-gray-900">Local Ontario Company</span>
              </div>
              <p className="text-gray-600 text-sm ml-11">300+ successful installations • Increase your home's value and outdoor living space</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <button type="button" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2" onClick={() => setStep(7)}>
            <ChevronRight size={20} /> Continue to Contact Details
          </button>
          <button type="button" className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-4 px-6 rounded-lg transition-colors" onClick={() => setShowExpensiveOptions(true)}>
            This is Too Expensive for Me
          </button>
        </div>

        {showExpensiveOptions && (
          <div className="mt-6 p-6 bg-orange-50 border border-orange-200 rounded-lg">
            <h3 className="font-semibold text-orange-900 mb-4">Let's Find a Solution That Works!</h3>
            <div className="space-y-3">

              <button type="button" className="w-full text-left p-4 hover:bg-white rounded-lg border border-orange-200 transition-colors" onClick={() => setFinancingModalOpen(true)}>
                <div className="font-semibold text-orange-900">💳 Explore Financing Options</div>
                <div className="text-orange-800 text-sm">As low as {currency(Math.round(totalMin / 12))}/month (estimated, O.A.C.)</div>
              </button>
              {formData.addOns.length > 0 && (
                <button type="button" className="w-full text-left p-4 hover:bg-white rounded-lg border border-orange-200 transition-colors" onClick={() => { setShowExpensiveOptions(false); setStep(5); }}>
                  <div className="font-semibold text-orange-900">🎯 Remove Add-ons for Now</div>
                  <div className="text-orange-800 text-sm">Go back to add-ons page to deselect features and reduce cost</div>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 font-medium">🔥 Limited Time: Book your site visit this month and save $200 on installation!</p>
          <p className="text-yellow-700 text-sm mt-1">Join 500+ happy Ontario homeowners who transformed their outdoor space</p>
        </div>
      </div>
    );
  };

  const renderStep8 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Pergola Quote</h2>

        <div className="grid md:grid-cols-2 gap-4 text-left">
          <div className="p-4 border rounded-lg">
            <h3 className="font-semibold mb-2">Summary</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li className="flex justify-between items-center py-1">
                <span><span className="text-gray-500">Size:</span> {formData.size || '—'}</span>
                <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(1)}>
                  Change
                </button>
              </li>
              <li className="flex justify-between items-center py-1">
                <span><span className="text-gray-500">Model:</span> {formData.model || '—'}</span>
                <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(2)}>
                  Change
                </button>
              </li>
              <li className="flex justify-between items-center py-1">
                <span><span className="text-gray-500">Color:</span> {formData.color ? PERGOLA_COLORS.find(c => c.id === formData.color)?.name || formData.color : '—'}</span>
                <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(3)}>
                  Change
                </button>
              </li>
              <li className="flex justify-between items-center py-1">
                <span><span className="text-gray-500">Location:</span> {formData.location || '—'}</span>
                <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(4)}>
                  Change
                </button>
              </li>
              <li className="flex justify-between items-center py-1">
                <span><span className="text-gray-500">Add-Ons:</span> {formData.addOns.length ? formData.addOns.length : 0}</span>
                <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(5)}>
                  Change
                </button>
              </li>
              <li className="flex justify-between items-center py-1">
                <span><span className="text-gray-500">Address:</span> {formData.city && formData.province ? `${formData.city}, ${formData.province}` : '—'}</span>
                <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(7)}>
                  Change
                </button>
              </li>
            </ul>
          </div>
          <div className="p-4 border rounded-lg">
            <h3 className="font-semibold mb-2">Price Breakdown</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Base pergola: <span className="font-semibold">{currency(basePrice)}</span></li>
              <li>Priced add-ons: <span className="font-semibold">{currency(addOnsTotal)}</span></li>
              {installCost > 0 && (<li>Deck Install: <span className="font-semibold">+{currency(installCost)}</span></li>)}
              {deliveryFee > 0 && (<li>Delivery: <span className="font-semibold">{currency(deliveryFee)}</span></li>)}
              <li>Special site work: quoted separately{formData.siteWorkNotes ? ` — ${formData.siteWorkNotes}` : ' if required'}</li>
              <li className="border-t pt-2">Subtotal: <span className="font-semibold">{currency(subtotal)}</span></li>
              <li>HST (13%): <span className="font-semibold">{currency(hst)}</span></li>
              <li className="border-t pt-2 font-semibold">Total: <span className="font-semibold">{currency(totalWithHST)}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-lg">
          <p className="text-lg mb-2">Your Estimate</p>
          <p className="text-4xl font-bold" aria-live="polite">{currency(totalWithHST)}</p>
          <p className="text-sm opacity-90 mt-1">Includes tax, standard installation and delivery. Special site work and unpriced options are additional.</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex gap-4 mb-6">
          <button
            type="button"
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            onClick={() => setStep(7)}
          >
            <ChevronLeft size={20} /> Back to Contact Info
          </button>
          <button
            type="button"
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            onClick={() => setStep(6)}
          >
            ← Back to Summary
          </button>
        </div>

        <button
          type="button"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          disabled={emailSending}
          onClick={async () => {
            // Check if we have contact information
            if (!formData.name || !formData.email || !formData.phone) {
              setMsg('Please provide your contact information first.');
              setStep(7); // Go to contact form
              return;
            }

            setEmailSending(true);
            try {
              // Prepare quote submission data
              const quoteData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                street_address: formData.streetAddress,
                city: formData.city,
                province: formData.province,
                postal_code: formData.postalCode,
                pergola_model: formData.model,
                pergola_size: formData.size,
                pergola_color: formData.color,
                pergola_location: formData.location,
                selected_addons: formData.addOns.map(id => {
              const option = addons.find(item => item.id === id);
              return isPrivacyPanelAddon(id)
                ? id + ' [' + (formData.privacyPanelSides[id] || []).join(', ') + '; ' + countPrivacyPanels(formData.size, formData.privacyPanelSides[id] || []) + ' panels' + (option?.perLinearFoot ? '; ' + getScreenFeet(formData.size, formData.privacyPanelSides[id] || []).toFixed(2) + ' linear ft' : '') + (option?.quoteOnly ? '; quote separately' : '') + ']'
                : id + ' [quantity ' + (formData.addOnQuantities[id] || 1) + (option?.quoteOnly ? '; quote separately' : '') + ']';
            }).concat(formData.siteWorkNotes.trim() ? ['Special site work (quoted separately): ' + formData.siteWorkNotes.trim()] : []),
                base_price: basePrice,
                addons_total: addOnsTotal,
                install_cost: installCost,
                delivery_fee: deliveryFee,
                subtotal: subtotal,
                hst: hst,
                total_with_hst: totalWithHST,
                total_min: totalMin,
                total_max: totalMax,
                action: 'email-quote',
                source: 'pergola_quote_form',
                referrer: document.referrer,
                user_agent: navigator.userAgent
              };

              console.log('Attempting to save quote data:', quoteData);

              // Validate data types before sending
              const validatedData = {
                ...quoteData,
                base_price: Number(quoteData.base_price) || 0,
                addons_total: Number(quoteData.addons_total) || 0,
                install_cost: Number(quoteData.install_cost) || 0,
                delivery_fee: Number(quoteData.delivery_fee) || 0,
                subtotal: Number(quoteData.subtotal) || 0,
                hst: Number(quoteData.hst) || 0,
                total_with_hst: Number(quoteData.total_with_hst) || 0,
                total_min: Number(quoteData.total_min) || 0,
                total_max: Number(quoteData.total_max) || 0,
              };

              // Submit to Supabase
              const { data, error } = await supabase
                .from('quote_submissions')
                .insert([validatedData])
                .select();

              console.log('Supabase response:', { data, error });

              if (error) {
                console.error('Supabase error details:', error);
                console.error('Error details:', error.details, error.hint, error.message);
                throw error;
              }

              console.log('Quote submission saved successfully:', data);

              // Google Analytics tracking
              if (typeof (window as any).gtag !== 'undefined') {
                (window as any).gtag('event', 'quote_email_requested', {
                  event_category: 'lead_generation',
                  event_label: `${formData.model}_${formData.size}`,
                  value: Math.round(totalWithHST),
                  custom_parameters: {
                    pergola_model: formData.model,
                    pergola_size: formData.size,
                    total_amount: totalWithHST,
                    submission_id: data[0]?.id,
                    utm_source: utm.source,
                    utm_medium: utm.medium,
                    utm_campaign: utm.campaign,
                    utm_ref: utm.ref
                  }
                });
              }

              setEmailSent(true);
              setMsg('✅ Quote submitted! We will email your detailed pergola quote within 24 hours. Check your inbox (and spam folder).');
            } catch (error) {
              console.error('Error submitting quote:', error);
              setMsg('❌ We had trouble submitting your quote. Please try again or contact us directly.');
            } finally {
              setEmailSending(false);
            }
          }}
        >
          <Mail size={20} />
          {emailSending ? 'Sending...' : emailSent ? '✅ Quote Request Sent!' : 'Send Me This Quote by Email'}
        </button>

        <button
          type="button"
          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          onClick={async () => {
            // Check if we have contact information
            if (!formData.name || !formData.email || !formData.phone) {
              setMsg('Please provide your contact information first to book a consultation.');
              setStep(7); // Go to contact form
              return;
            }

            try {
              // Prepare consultation booking data
              const consultationData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                street_address: formData.streetAddress,
                city: formData.city,
                province: formData.province,
                postal_code: formData.postalCode,
                pergola_model: formData.model,
                pergola_size: formData.size,
                pergola_color: formData.color,
                pergola_location: formData.location,
                selected_addons: formData.addOns.map(id => {
              const option = addons.find(item => item.id === id);
              return isPrivacyPanelAddon(id)
                ? id + ' [' + (formData.privacyPanelSides[id] || []).join(', ') + '; ' + countPrivacyPanels(formData.size, formData.privacyPanelSides[id] || []) + ' panels' + (option?.perLinearFoot ? '; ' + getScreenFeet(formData.size, formData.privacyPanelSides[id] || []).toFixed(2) + ' linear ft' : '') + (option?.quoteOnly ? '; quote separately' : '') + ']'
                : id + ' [quantity ' + (formData.addOnQuantities[id] || 1) + (option?.quoteOnly ? '; quote separately' : '') + ']';
            }).concat(formData.siteWorkNotes.trim() ? ['Special site work (quoted separately): ' + formData.siteWorkNotes.trim()] : []),
                base_price: basePrice,
                addons_total: addOnsTotal,
                install_cost: installCost,
                delivery_fee: deliveryFee,
                subtotal: subtotal,
                hst: hst,
                total_with_hst: totalWithHST,
                total_min: totalMin,
                total_max: totalMax,
                action: 'book-consultation',
                source: 'pergola_quote_form',
                referrer: document.referrer,
                user_agent: navigator.userAgent
              };

              console.log('Attempting to save consultation data:', consultationData);

              // Validate data types before sending
              const validatedConsultationData = {
                ...consultationData,
                base_price: Number(consultationData.base_price) || 0,
                addons_total: Number(consultationData.addons_total) || 0,
                install_cost: Number(consultationData.install_cost) || 0,
                delivery_fee: Number(consultationData.delivery_fee) || 0,
                subtotal: Number(consultationData.subtotal) || 0,
                hst: Number(consultationData.hst) || 0,
                total_with_hst: Number(consultationData.total_with_hst) || 0,
                total_min: Number(consultationData.total_min) || 0,
                total_max: Number(consultationData.total_max) || 0,
              };

              // Submit to Supabase
              const { data, error } = await supabase
                .from('quote_submissions')
                .insert([validatedConsultationData])
                .select();

              console.log('Supabase consultation response:', { data, error });

              if (error) {
                console.error('Supabase consultation error details:', error);
                throw error;
              }

              console.log('Consultation booking saved successfully:', data);

              // Google Analytics tracking
              if (typeof (window as any).gtag !== 'undefined') {
                (window as any).gtag('event', 'consultation_booking_requested', {
                  event_category: 'lead_generation',
                  event_label: `${formData.model}_${formData.size}`,
                  value: Math.round(totalWithHST),
                  custom_parameters: {
                    pergola_model: formData.model,
                    pergola_size: formData.size,
                    total_amount: totalWithHST,
                    submission_id: data[0]?.id,
                    utm_source: utm.source,
                    utm_medium: utm.medium,
                    utm_campaign: utm.campaign,
                    utm_ref: utm.ref
                  }
                });
              }

              setMsg('✅ Your information has been saved! Opening Calendly to book your consultation...');

              // Open Calendly after a short delay to show the success message
              setTimeout(() => {
                window.open('https://calendly.com/evieoutdoors', '_blank');
              }, 1000);

            } catch (error) {
              console.error('Error saving consultation request:', error);
              setMsg('❌ We had trouble saving your information. Please try again or contact us directly.');
            }
          }}
        >
          <Calendar size={20} /> Book a Consultation / Site Visit
        </button>

        <button
          type="button"
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
          onClick={() => setShowNotInterestedOptions(true)}
        >
          <X size={20} /> Not Interested
        </button>
      </div>

      {showNotInterestedOptions && (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-4">What's the main reason?</h3>
          <div className="space-y-2">
            <button type="button" className="w-full text-left p-3 hover:bg-white rounded border transition-colors" onClick={() => setMsg('Here are budget-friendly options.')}>
              💰 Price looks high? → <span className="text-blue-600">review size and options</span>
            </button>
            <button type="button" className="w-full text-left p-3 hover:bg-white rounded border transition-colors" onClick={() => setMsg('We will follow up in spring.')}>
              🌸 Not right now → <span className="text-blue-600">Follow up in spring?</span>
            </button>
            <button type="button" className="w-full text-left p-3 hover:bg-white rounded border transition-colors" onClick={() => setMsg('Opening our Ontario project gallery…')}>
              👀 Just browsing → <span className="text-blue-600">Hold this price for me</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Show loading state while pricing data is being fetched
  if (pricingLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white min-h-screen pb-24">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading pricing information...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if pricing data failed to load
  if (pricingError) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white min-h-screen pb-24">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Unable to Load Pricing</h2>
            <p className="text-red-600 mb-4">We're having trouble loading our current pricing. Please try refreshing the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white min-h-screen pb-24">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Get Your Pergola Quote</h1>
        <p className="text-gray-600">Design your perfect outdoor space in just a few steps</p>
        <div className="text-sm text-gray-600 mt-2">Premium · Motorized louvers · Perimeter lighting · {PERGOLA_HEIGHT}</div>
      </div>

      {renderProgressBar()}

      <div className="mb-8">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
        {step === 6 && renderStep7()} {/* Quote summary */}
        {step === 7 && renderStep6()} {/* Contact info */}
        {step === 8 && renderStep8()}
      </div>



      {step < 8 && (
        <div className="flex justify-between">
          <button type="button" onClick={prevStep} disabled={step === 1} className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
            <ChevronLeft size={20} /> Previous
          </button>

          <button
            type="button"
            onClick={() => {
              console.log('Submit/Next button clicked, current step:', step);
              nextStep();
            }}
            disabled={!canProceed() || isStepping}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors min-w-[120px] justify-center"
          >
            {isStepping ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                {step === 7 ? 'Submit' : 'Next'} {step !== 7 && <ChevronRight size={20} />}
              </>
            )}
          </button>
        </div>
      )}

      {/* Financing Modal */}
      {financingModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Financing Options</h3>
                <button
                  onClick={() => setFinancingModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-6">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Your Quote Summary</h4>
                  <div className="text-blue-800">
                    <div>{formData.model.charAt(0).toUpperCase() + formData.model.slice(1)} Pergola - {formData.size}</div>
                    <div className="text-lg font-bold">Total: {currency(totalMax)}</div>
                  </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg mb-4 border border-orange-200">
                  <h4 className="font-semibold text-orange-900 mb-2">Financing Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-orange-800">Required Deposit:</span>
                      <span className="font-semibold text-orange-900">{currency(requiredDeposit)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-orange-800">Amount to Finance:</span>
                      <span className="font-semibold text-orange-900">{currency(financingAmount)}</span>
                    </div>
                  </div>
                  <div className="text-xs text-orange-700 mt-2">
                    * Deposit required to secure materials and cover project costs
                  </div>
                </div>

                <h4 className="font-semibold text-gray-900 mb-2">Choose Your Payment Plan</h4>
                <div className="text-sm text-gray-600 mb-4">Monthly payments for financed amount ({currency(financingAmount)})</div>
                <div className="grid gap-4">
                  {financingOptions.map((option) => (
                    <div key={option.term} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-semibold text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600">{option.description}</div>
                          {option.rate === 0 && (
                            <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full inline-block mt-1">
                              Special Offer
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600">
                            {currency(option.payment)}
                          </div>
                          <div className="text-sm text-gray-600">/month</div>
                          <div className="text-xs text-gray-500">+ {currency(requiredDeposit)} deposit</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-semibold text-gray-900 mb-4">Get Pre-Approved Today</h4>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    value={financingContactInfo.name}
                    onChange={(e) => setFinancingContactInfo(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    value={financingContactInfo.email}
                    onChange={(e) => setFinancingContactInfo(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  className="w-full p-3 border border-gray-300 rounded-lg mb-4"
                  value={financingContactInfo.phone}
                  onChange={(e) => setFinancingContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                />

                <div className="text-xs text-gray-600 mb-4">
                  * Subject to credit approval (O.A.C.). Financing provided by third-party lenders.
                  A financing specialist will contact you within 24 hours to discuss options and complete your application.
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setFinancingModalOpen(false)}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleFinancingSubmit}
                    className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Get Pre-Approved
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Financing Success Modal */}
      {financingSuccessOpen && submittedFinancingData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 text-center">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>

              {/* Success Message */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Financing Request Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you, {submittedFinancingData.name}! A financing specialist will contact you within 24 hours to discuss your options.
              </p>

              {/* Summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                <h4 className="font-semibold text-gray-900 mb-3">Your Request Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pergola:</span>
                    <span className="font-medium">{submittedFinancingData.pergola_model.charAt(0).toUpperCase() + submittedFinancingData.pergola_model.slice(1)} - {submittedFinancingData.pergola_size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-medium">{currency(submittedFinancingData.total_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Required Deposit:</span>
                    <span className="font-medium">{currency(submittedFinancingData.required_deposit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Financing Amount:</span>
                    <span className="font-medium text-blue-600">{currency(submittedFinancingData.financing_amount)}</span>
                  </div>
                  {submittedFinancingData.submissionId && (
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-gray-600">Reference ID:</span>
                      <span className="font-mono text-xs">{submittedFinancingData.submissionId}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6 text-left">
                <h4 className="font-semibold text-blue-900 mb-2">What Happens Next?</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• A financing specialist will call you within 24 hours</li>
                  <li>• They'll review your options and help with the application</li>
                  <li>• Pre-approval typically takes 1-2 business days</li>
                  <li>• We'll coordinate installation once financing is approved</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setFinancingSuccessOpen(false);
                    setSubmittedFinancingData(null);
                  }}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back to Quote
                </button>
                <button
                  onClick={() => {
                    // Navigate to home page
                    window.location.href = '/';
                  }}
                  className="flex-1 py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Back to Home Page
                </button>
              </div>

              {/* Contact Info */}
              <div className="mt-4 text-xs text-gray-500">
                Questions? Call us at <span className="font-medium">(416) 555-0123</span> or email <span className="font-medium">financing@evieoutdoors.com</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Submit Success Modal */}
      {contactSubmitSuccessOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 text-center">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>

              {/* Success Message */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Information Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you, {formData.name}! Your contact information has been saved. You can now view your final quote and choose how you'd like to proceed.
              </p>

              {/* Summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                <h4 className="font-semibold text-gray-900 mb-3">Your Quote Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pergola:</span>
                    <span className="font-medium">{formData.model.charAt(0).toUpperCase() + formData.model.slice(1)} - {formData.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-medium">{formData.city}, {formData.province}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Investment:</span>
                    <span className="font-medium text-blue-600">{currency(totalMax)}</span>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6 text-left">
                <h4 className="font-semibold text-blue-900 mb-2">Next Steps</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• View your detailed final quote</li>
                  <li>• Email the quote to yourself for review</li>
                  <li>• Book a consultation or site visit</li>
                  <li>• Explore financing options if needed</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setContactSubmitSuccessOpen(false);
                  }}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setContactSubmitSuccessOpen(false);
                    setStep(7); // Go to final quote
                  }}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View Final Quote
                </button>
              </div>

              {/* Contact Info */}
              <div className="mt-4 text-xs text-gray-500">
                Questions? Call us at <span className="font-medium">(416) 555-0123</span> or email <span className="font-medium">info@evieoutdoors.com</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {Toast}
    </div>
  );
};

export default PergolaQuoteForm;
